import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { getCurrentUser, fetchAuthSession, signOut as amplifySignOut } from '@aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';

interface DecodedIdToken {
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  "custom:paid"?: string;
  "custom:subscriptionTier"?: string;
}

interface User {
  email: string;
  name: string;
  isApproved: boolean;
  subscriptionTier?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isApproved: boolean;
  isLoading: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  refreshStatus: () => Promise<boolean>;
  clearError: () => void;
  setUser: (user: User | null) => void;
  isProcessingModalOpen: boolean; // Track if any processing modal is open
  setProcessingModalOpen: (isOpen: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Processing modal state
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState<boolean>(false);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setError(null);
      setIsProcessingModalOpen(false); // Reset processing modal state on logout
    } catch (err: any) {
      console.error('Error signing out:', err);
      setError(err.message || 'Failed to sign out');
    }
  }, []);

  const refreshStatus = useCallback(async (): Promise<boolean> => {
    try {
      console.log("=== refreshStatus called in AuthContext ===");
      setError(null);
      console.log("About to call getCurrentUser");
      await getCurrentUser();
      console.log("getCurrentUser successful, about to fetchAuthSession");
      const session = await fetchAuthSession({ forceRefresh: true });
      console.log("fetchAuthSession successful");
      const idToken = session.tokens?.idToken?.toString();
      
      if (idToken) {
        const decoded: DecodedIdToken = jwtDecode(idToken);
        
        // Construct full name, prioritizing actual name fields
        let fullName = "User";
        
        if (decoded.name) {
          fullName = decoded.name;
        } else if (decoded.given_name) {
          // If we have given name, use it with family name if available
          const firstName = decoded.given_name;
          const lastName = decoded.family_name || "";
          fullName = lastName ? `${firstName} ${lastName}` : firstName;
        } else if (decoded.family_name) {
          // Only family name available
          fullName = decoded.family_name;
        }
        
        const isApproved = decoded["custom:paid"] === "true";
        const subscriptionTier = decoded["custom:subscriptionTier"] || undefined;
        const userData: User = {
          email: decoded.email || '',
          name: fullName,
          isApproved,
          subscriptionTier,
        };
        
        setUser(userData);
        
        return isApproved;
      } else {
        console.error("No ID token found in session.");
        setUser(null);
        return false;
      }
    } catch (err: any) {
      console.error("Error in refreshStatus:", err);
      console.error("Error name in refreshStatus:", err.name);
      console.error("Error message in refreshStatus:", err.message);
      console.info("User not authenticated or session expired:", err);
      setUser(null);
      
      // Check if this is an unconfirmed user error
      if (err.name === 'UserUnAuthenticatedException' && err.message?.includes('needs to be authenticated')) {
        // This could be an unconfirmed user - re-throw so the caller can handle it
        setError('Your account is not confirmed. Please check your email for the verification code.');
        throw err;
      } else {
        setError(null); // Don't set error for normal auth failures
        return false;
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps



  // Initial auth check
  useEffect(() => {
    const checkAuthStatus = async () => {
      setIsLoading(true);
      try {
        await refreshStatus();
      } catch (error) {
        // Silently handle errors during initial auth check
        // This prevents errors from showing when the page loads and user is not authenticated
        console.info("Initial auth check failed (expected for unauthenticated users):", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [refreshStatus]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isApproved: user?.isApproved || false,
    isLoading,
    error,
    signOut,
    refreshStatus,
    clearError,
    setUser,
    isProcessingModalOpen,
    setProcessingModalOpen: setIsProcessingModalOpen,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 